import { useEffect, useState } from 'react';
import { api } from '@/api';
import type { QuizBank } from '@shared/schemas';

/**
 * useQuizBank · fetches a quiz bank (questions + answers + result-review metadata)
 * from the gated `/api/quiz/banks/:id` (slot_content_protection · quiz client
 * cutover 29 JUN 2026).
 *
 * Quiz banks used to ship in the client bundle, so a trial user could read every
 * sprint's final-test answers and the block-exam keys straight from the JS. Now
 * the bank is served only by the entitlement-gated edge `quiz` function, and the
 * client pulls it on demand here. The screen renders a loading state until it
 * arrives, a not-found/forbidden state if the server denies it.
 *
 * `state`:
 *   - 'loading' — request in flight
 *   - 'ready'   — `bank` is populated
 *   - 'denied'  — 403/404/network (not entitled, missing, or offline) · `bank` null
 */
export type UseQuizBankState = 'loading' | 'ready' | 'denied';

export interface UseQuizBankResult {
  bank: QuizBank | null;
  state: UseQuizBankState;
}

export function useQuizBank(bankId: string | undefined): UseQuizBankResult {
  const [bank, setBank] = useState<QuizBank | null>(null);
  const [state, setState] = useState<UseQuizBankState>('loading');

  useEffect(() => {
    if (!bankId) {
      setBank(null);
      setState('denied');
      return;
    }
    let alive = true;
    const controller = new AbortController();
    setState('loading');
    setBank(null);
    api.quiz
      .getBank(bankId, { signal: controller.signal })
      .then((b) => {
        if (!alive) return;
        setBank(b);
        setState('ready');
      })
      .catch(() => {
        if (!alive || controller.signal.aborted) return;
        setBank(null);
        setState('denied');
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [bankId]);

  return { bank, state };
}
